import {createContext, useCallback, useContext, useState} from 'react'

import Script from 'next/script'
import {useMount} from 'react-use'
import {palette} from 'styled-tools'
import styled from 'styled-components'
import PlayFillIcon from 'remixicon-react/PlayFillIcon'

export interface Image {
  url: string
  height: number
  width: number
}

export interface Video {
  id: string
  title: string
  type: string
  videoId: string
  genre: string
  duration: string
  isSilent: false
  language: string
  uploadDate: Date
  providerVideoId: string
  thumbs: {
    default: Image
    medium: Image
    high: Image
    standard: Image
    maxres: Image
  }
}

export interface Actions {
  startPlayer: (video: Video) => void
  closePlayer: () => void
}

export interface Data {
  videos: Video[]
  actions: Actions
}

const PlayButton = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 50%;
  left: 50%;
  width: 68px;
  height: 68px;
  transform: translate(-50%, -50%);
  z-index: 2;
  background-color: ${palette('green')};
  border: 5px solid white;
  border-radius: 50%;
  cursor: pointer;

  svg {
    fill: white;
  }

  &.small {
    width: 32px;
    height: 32px;
    border-width: 2px;
  }
`

export function VideolyPlayButton({size}: {size?: 'small' | 'large'}) {
  return (
    <PlayButton className={size}>
      <PlayFillIcon size={size !== 'small' ? 32 : 16} />
    </PlayButton>
  )
}

declare global {
  interface Window {
    videolyInitCallbacks?: {
      onVideoDataLoaded: ((err: unknown, data: Data) => void)[]
    }
  }
}

export function VideolyScript() {
  return (
    <Script
      id="videoly-script"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
      (function(d,e,n){
        e = d.createElement('script');
        e.type = 'text/javascript';
        e.async = true;
        e.src = '//api.videoly.co/1/quchbox/0/412/quch.js';
        n = d.getElementsByTagName('script')[0];
        n.parentNode.insertBefore(e,n);
        })(document);
        `,
      }}
    />
  )
}

export const VideolyContext = createContext<Partial<VideolyContextProps>>({})

export interface VideosBySku {
  [sku: string]: Video[]
}

interface VideolyProviderProps {
  children: React.ReactNode
}

export function VideolyProvider({children}: VideolyProviderProps) {
  const [loading, setLoading] = useState(true)
  const [currSku, setCurrSku] = useState<string>(null)
  const [actions, setActions] = useState<Actions>(null)
  const [videolyVideos, setVideolyVideos] = useState<Video[]>(null)

  const handleLoading = useCallback(
    (sku: string) => {
      if (currSku !== sku) {
        setLoading(true)
        setCurrSku(sku)
      }
    },
    [currSku],
  )

  const handleStartPlayer = useCallback(
    (video: Video) => {
      if (actions?.startPlayer) {
        actions.startPlayer(video)
      }
    },
    [actions],
  )

  const handleClosePlayer = useCallback(() => {
    if (actions?.closePlayer) {
      actions.closePlayer()
    }
  }, [actions])

  useMount(() => {
    const onVideoDataLoaded = (err: unknown, data: Data) => {
      if (err) {
        console.error('Videoly data error:', err)
        return
      }
      const {videos, actions} = data

      setLoading(false)
      setActions(actions)
      setVideolyVideos(videos)
    }

    window.videolyInitCallbacks = {
      onVideoDataLoaded: [onVideoDataLoaded],
    }
  })

  return (
    <VideolyContext.Provider
      value={{
        actions,
        videolyVideos: loading ? [] : videolyVideos,
        handleLoading,
        handleStartPlayer,
        handleClosePlayer,
      }}
    >
      {children}

      <VideolyScript />
    </VideolyContext.Provider>
  )
}

export interface VideolyContextProps {
  actions: Actions
  videolyVideos: Video[]
  handleLoading: (sku: string) => void
  handleStartPlayer: (video: Video) => void
  handleClosePlayer: () => void
}

export function useVideoly() {
  return useContext(VideolyContext) as VideolyContextProps
}
